import React, { useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  openBetSelectPopup,
  closeBetSelectPopup,
} from "../../../../State/actions";
import {
  selectBetSelectPopupActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectIsSpinning,
  selectAutoPlayActive,
} from "../../../../State/selectors";

// Bet select button configuration - matches UI button dimensions
const BET_SELECT_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.97,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 0.9,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const ButtonBetSelect = ({ toolbar }) => {
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBetSelectPopupActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  const texture = PIXI.Assets.get(ASSET_NAME.icon_bet_select);

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(BET_SELECT_BUTTON_CONFIG, true);

  // Calculate responsive button position based on device and orientation
  // Uses toolbar width instead of full screen width
  const buttonPosition = (() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width / 1.5,
          y: -dimensions.height / 5.2 + buttonConfig.height / 2,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width * 0.775,
          y: -dimensions.height / 2 - buttonConfig.height * 0.65,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width * 0.9,
          y: -dimensions.height / 5.2 + buttonConfig.height / 2,
        };
      } else {
        return {
          x: buttonConfig.width * 5.6,
          y: toolbar.height / 2,
        };
      }
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2,
      };
    }
  })();

  // Calculate if button should be disabled - prevent during spins, critical animations, or autoplay
  const isDisabled =
    criticalAnimationPlaying ||
    holdAndWinActive ||
    isSpinning ||
    autoPlayActive;

  const handleClick = () => {
    // Disable button during critical animations or hold and win feature
    if (isDisabled) {
      return;
    }

    // Don't open bet select popup on desktop
    if (device === PixiSettings.DEVICE.DESKTOP) {
      return;
    }

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    // Toggle bet select popup - close if open, open if closed
    if (popupActive) {
      dispatch(closeBetSelectPopup());
    } else {
      dispatch(openBetSelectPopup());
    }
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (isDisabled) {
      return;
    }
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter, isDisabled]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (isDisabled && buttonSpriteRef.current) {
      buttonSpriteRef.current.filters = [];
      isHoveredRef.current = false;
    }
  }, [isDisabled]);

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
    };
  }, []);

  if (device === PixiSettings.DEVICE.DESKTOP) {
    return null;
  }

  return (
    <Sprite
      ref={buttonSpriteRef}
      texture={texture}
      name="secondary_button_bet_select"
      anchor={0.5}
      width={buttonConfig.width}
      height={buttonConfig.height}
      x={buttonPosition.x}
      y={buttonPosition.y}
      eventMode={isDisabled ? "none" : "static"}
      cursor={isDisabled ? "default" : "pointer"}
      alpha={isDisabled ? 0.7 : 1}
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
    />
  );
};

export default ButtonBetSelect;
